/**
 * Native `position: sticky` fails whenever an ancestor is a scroll container — most often when a
 * theme puts `overflow-x: hidden` on BOTH html and body (body then stops propagating to the
 * viewport and becomes the sticky element's never-scrolling container). We detect that case and
 * emulate sticky with fixed/absolute positioning; otherwise native sticky is left alone.
 */
export type StickyPhase = 'top' | 'fixed' | 'bottom';

/** Pure: where the panel should be for the current scroll position. */
export function stickyPhase(layoutTop: number, layoutBottom: number, panelHeight: number, offset: number): StickyPhase {
  if (layoutTop >= offset) return 'top';
  if (layoutBottom - panelHeight <= offset) return 'bottom';
  return 'fixed';
}

// Browsers always compute a value; an empty string (jsdom, or a detached element) means "unset" = visible.
const nonVisible = (value: string) => value !== '' && value !== 'visible';
const scrolls = (style: CSSStyleDeclaration) => nonVisible(style.overflowX) || nonVisible(style.overflowY) || nonVisible(style.overflow);

/** True when an ancestor (below the viewport) would capture sticky positioning. */
export function isNativeStickyBlocked(el: Element, view: Window = window): boolean {
  const doc = el.ownerDocument;
  const html = doc.documentElement;
  const body = doc.body;
  const htmlScrolls = scrolls(view.getComputedStyle(html));
  for (let node = el.parentElement; node; node = node.parentElement) {
    if (node === html) break;
    const style = view.getComputedStyle(node);
    if (!scrolls(style)) continue;
    // body's overflow normally propagates to the viewport — unless html already has its own.
    if (node === body) return htmlScrolls;
    return true;
  }
  return false;
}

/** Height of the WordPress admin bar when present (WP exposes it as a CSS variable). */
export function adminBarOffset(doc: Document = document): number {
  const value = doc.defaultView?.getComputedStyle(doc.documentElement).getPropertyValue('--wp-admin--admin-bar--height').trim();
  const parsed = value ? Number.parseFloat(value) : NaN;
  if (Number.isFinite(parsed)) return parsed;
  return doc.getElementById('wpadminbar')?.offsetHeight ?? 0;
}

export interface StickyOptions {
  /** the grid cell that stays in flow (placeholder) */
  cell: HTMLElement;
  /** the panel that gets fixed/absolute positioned */
  panel: HTMLElement;
  /** the tall sibling column the panel should track */
  layout: HTMLElement;
  gap: number;
  minWidth: number;
}

/** Starts the fallback; returns a cleanup. No-op when native sticky works or the viewport is narrow. */
export function startStickyFallback({ cell, panel, layout, gap, minWidth }: StickyOptions): () => void {
  if (!isNativeStickyBlocked(cell)) return () => {};
  cell.classList.add('is-js-sticky');
  let raf = 0;

  const reset = () => {
    panel.style.position = '';
    panel.style.top = '';
    panel.style.bottom = '';
    panel.style.left = '';
    panel.style.width = '';
  };

  const update = () => {
    raf = 0;
    if (window.innerWidth < minWidth) {
      reset();
      return;
    }
    const offset = gap + adminBarOffset(cell.ownerDocument);
    const layoutRect = layout.getBoundingClientRect();
    const cellRect = cell.getBoundingClientRect();
    const phase = stickyPhase(layoutRect.top, layoutRect.bottom, panel.offsetHeight, offset);
    if (phase === 'top') {
      reset();
    } else if (phase === 'fixed') {
      panel.style.position = 'fixed';
      panel.style.top = `${offset}px`;
      panel.style.bottom = '';
      panel.style.left = `${Math.round(cellRect.left)}px`;
      panel.style.width = `${Math.round(cellRect.width)}px`;
    } else {
      panel.style.position = 'absolute';
      panel.style.top = '';
      panel.style.bottom = '0';
      panel.style.left = '0';
      panel.style.width = '100%';
    }
  };
  const schedule = () => {
    if (!raf) raf = requestAnimationFrame(update);
  };
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
  observer?.observe(layout);
  observer?.observe(panel);
  update();

  return () => {
    window.removeEventListener('scroll', schedule);
    window.removeEventListener('resize', schedule);
    observer?.disconnect();
    if (raf) cancelAnimationFrame(raf);
    reset();
    cell.classList.remove('is-js-sticky');
  };
}
